import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import ProviderHeader from 'components/visit-creation/provider-info/ProviderHeader'
import { getActualVisitType, iconForService } from 'util/visit-types'
import { EVisitModalities, VisitModalityIcons } from 'util/visit-modalities'
import VisitTypes, { DisplayVisitType } from 'util/visit-types'
import { VisitModalityLabels } from 'util/visit-modalities'
import Provider from 'types/provider/provider'
import PendingVisit from 'types/visit/pendingVisit'
import AddToCalendarTag from 'components/dashboard/VisitCard/AddToCalendarTag'

type VisitInfoCardProps = {
  title: string
  scheduledAt?: string
  timezone: string
  provider?: Provider
  visitType: string
  scheduledType: string
  modality: string
  className?: string
  showAddToCalendar?: boolean
  pendingVisit?: PendingVisit
  getCalendarInfo?: () => Promise<void>
}

const VisitInfoCard = ({
  title,
  scheduledAt,
  timezone,
  provider,
  visitType,
  scheduledType,
  modality,
  className = '',
  showAddToCalendar = false,
  pendingVisit,
  getCalendarInfo,
}: VisitInfoCardProps) => {
  const { t } = useTranslation()

  const actualVisitType = useMemo<string>(
    () => getActualVisitType(visitType, scheduledType),
    [visitType, scheduledType],
  )

  const visitTypeLabel = useMemo(() => {
    return t(DisplayVisitType[actualVisitType] || '')
  }, [actualVisitType, t])

  const modalityLabel = useMemo(() => {
    return t(VisitModalityLabels[modality as EVisitModalities])
  }, [modality, t])

  const getVisitTypeIcon = useMemo(() => iconForService(actualVisitType), [actualVisitType])
  const getModalityIcon = useMemo(() => VisitModalityIcons[modality as EVisitModalities], [modality])

  const displayAppointmentTime = useMemo(() => {
    if (!scheduledAt) return null
    return (
      moment(scheduledAt).tz(timezone).format('ddd, MMM Do') +
      ' ' +
      t('pendingVisitModal.at') +
      ' ' +
      moment(scheduledAt).tz(timezone).format('h:mm A')
    )
  }, [scheduledAt, timezone, t])

  const isScheduled = useMemo(() => {
    return scheduledType === 'scheduled'
  }, [scheduledType])

  return (
    <div className={`border-primary-600 rounded-lg border p-4 ${className}`}>
      <p className="typography-body mb-2 font-semibold uppercase text-neutral-500">{title}</p>

      {!!displayAppointmentTime && (
        <h4 className="typography-h4 mb-4 text-neutral-800" data-testid="appointment-time">
          {displayAppointmentTime}
        </h4>
      )}

      {!!provider && (
        <div className="mb-4">
          <ProviderHeader provider={provider} />
        </div>
      )}

      <div className="space-y-2">
        <div className="flex items-center">
          <span className="mr-2 text-teal-700">
            {getVisitTypeIcon({ width: 24, height: 24, 'aria-hidden': 'true' })}
          </span>
          <span className="typography-body-l text-neutral-800">{visitTypeLabel}</span>
        </div>
        <div className="flex items-center">
          <span className="mr-2 text-teal-700">
            {getModalityIcon({ width: 24, height: 24, 'aria-hidden': 'true' })}
          </span>
          <span className="typography-body-l text-neutral-800">{modalityLabel}</span>
        </div>
      </div>

      {showAddToCalendar && isScheduled && pendingVisit && getCalendarInfo && (
        <>
          <div className="my-4 h-[1px] bg-neutral-300" />
          <div data-testid="add-to-calendar-section">
            <AddToCalendarTag pendingVisit={pendingVisit} getCalendarInfo={getCalendarInfo} variant="modal" />
          </div>
        </>
      )}
    </div>
  )
}

export default VisitInfoCard
